import { isCpfValid } from "@repo/lib";
import { z } from "zod";
import GooglePhoneNumber from "google-libphonenumber";

const phoneNumberUtil = GooglePhoneNumber.PhoneNumberUtil.getInstance();

export const EventSchema = z.object({
  id: z.string(),
  type: z.string(),
  title: z.string(),
  appointmentType: z.string(),
  appointmentPurposeId: z.number().nullable().optional(),
  unit: z.object({
    id: z.number(),
    name: z.string(),
    acronym: z.string(),
  }),
  start: z.date(),
  end: z.date(),
  distance: z.number(),
  timeSlotType: z.number().optional(),
});

export const appointmentBasicInfo = z.object({
  unit: z.number().min(1, { message: "Campo obrigatório" }),
  appointmentType: z.number().min(1, { message: "Campo obrigatório" }),
  paymentMethod: z.number().min(1, { message: "Campo obrigatório" }),
  appointmentDate: z.date().optional(),
  appointmentTime: z.object({
    start: z.date(),
    end: z.date(),
    timeSlotType: z.number().optional(),
  }),
  scheduleId: z.number().optional(),
  unitName: z.string().optional(),
  unitAcronym: z.string().optional(),
  appointmentName: z.string().optional(),
  showAppointmentInfo: z.boolean().optional(),
  availableLocationsTimeSlots: z.array(EventSchema).optional(),
  selectedLocationTimeSlots: z.array(EventSchema).optional(),
  selectedDate: z.string().optional(),
});

export const patientInfo = z.object({
  patientId: z.number().optional(),
  phone: z
    .string({ required_error: "Você precisa informar um número de telefone." })
    .min(1, { message: "Você precisa informar um número de telefone." })
    .refine(
      (value) => /^\(\d{2}\) \d{4,5}-\d{4}$/.test(value),
      "Número de telefone inválido.",
    ),
  phoneCountry: z.string().optional(),
  name: z
    .string({ required_error: "Você precisa informar um nome" })
    .min(1, { message: "Você precisa informar um nome" }),
  dateOfBirth: z.date().optional(),
  email: z
    .string({ required_error: "Você precisa informar um e-mail" })
    .min(1, { message: "Você precisa informar um e-mail" })
    .email({ message: "E-mail inválido." }),
  cpf: z
    .string()
    .refine((value) => !value || isCpfValid(value), "CPF inválido.")
    .optional(),
  sendConfirmationViaWhatsApp: z.boolean(),
  roomTypes: z.number().array().optional(),
});

const patientInfoV2Base = z.object({
  patientId: z.number().optional(),
  phone: z
    .string({ required_error: "Você precisa informar um número de telefone." })
    .min(1, { message: "Você precisa informar um número de telefone." })
    .refine((value) => /^\+\d{10,}$/.test(value), "Número de telefone inválido."),
  phoneCountry: z.string().optional(),
  name: z
    .string({ required_error: "Você precisa informar um nome" })
    .min(1, { message: "Você precisa informar um nome" }),
  dateOfBirth: z.date().optional(),
  email: z
    .string({ required_error: "Você precisa informar um e-mail" })
    .min(1, { message: "Você precisa informar um e-mail" })
    .email({ message: "E-mail inválido." }),
  cpf: z
    .string()
    .refine((value) => !value || isCpfValid(value), "CPF inválido.")
    .optional(),
  sendConfirmationViaWhatsApp: z.boolean(),
  roomTypes: z.number().array().optional(),
});

export const patientInfoV2 = patientInfoV2Base.superRefine((values, context) => {
  if (
    !phoneNumberUtil.isValidNumberForRegion(
      phoneNumberUtil.parse(values.phone),
      values.phoneCountry ?? "BR",
    )
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Número de telefone inválido.",
      path: ["phone"],
    });
  }
});

export const flexiblePricingInfo = z.object({
  appointmentMode: z.number().optional(),
});

export const rescheduleInfo = z.object({
  appointmentId: z.number().optional(),
});

export const appointmentFormSchema = appointmentBasicInfo
  .merge(patientInfo)
  .merge(rescheduleInfo)
  .merge(flexiblePricingInfo);

export const appointmentFormSchemaV2 = appointmentBasicInfo
  .merge(patientInfoV2Base)
  .merge(rescheduleInfo)
  .merge(flexiblePricingInfo);
